import {IntlShape} from 'react-intl';
import {plannedInstanceCount} from './useTaskSetPreview';

/**
 * The persisted shape of a set, as far as describing it requires. Deliberately
 * structural rather than a Relay type: the panel reads it from a fragment and the
 * tests build it as a literal, and one description implementation has to serve
 * both.
 *
 * Every field is nullable because every column but the id is nullable in storage.
 */
export interface DescribableTaskSet {
	frequency?: string | null;
	intervalLength?: number | null;
	/** WEEKLY only. Several days are possible -- "every weekday" is five. */
	weekdays?: readonly (string | null)[] | null;
	/** MONTHLY weekday modes only, and exactly one day. */
	monthlyWeekday?: string | null;
	monthlyMode?: string | null;
	dayOfMonth?: number | null;
	weekOrdinal?: number | null;
	boundType?: string | null;
	/** A number of cycles of the pattern, not of instances -- see plannedInstanceCount. */
	boundCount?: number | null;
	boundEndDate?: string | null;
	/** Needed to size a weekly set's partial first week. */
	seedDate?: string | null;
	/** How far generation got. For a fully generated count-bounded set this is
	 *  the last instance's date, which is the only place that date exists. */
	materialisedTo?: string | null;
	/** The instance the set ends after, which is what Ends names. */
	lastInstance?: {companyTaskId?: number | null; startDate?: string | null} | null;
}

const DAY_ORDER = ['MONDAY', 'TUESDAY', 'WEDNESDAY', 'THURSDAY', 'FRIDAY', 'SATURDAY', 'SUNDAY'];

const ORDINAL_KEYS: {[ordinal: number]: string} = {
	1: 'task_set.ordinal_first',
	2: 'task_set.ordinal_second',
	3: 'task_set.ordinal_third',
	4: 'task_set.ordinal_fourth',
};

const UNIT_KEY_BY_FREQUENCY: {[frequency: string]: string} = {
	DAILY: 'task_set.pattern_days',
	WEEKLY: 'task_set.pattern_weeks',
	MONTHLY: 'task_set.pattern_months',
	YEARLY: 'task_set.pattern_years',
};

/**
 * Weekday names in Monday-first order, whatever order storage returned them in.
 *
 * Sorting here rather than trusting the input: the backend reads the seven flags
 * in Monday order, but a caller building this from a form is under no such
 * constraint, and "Fri, Mon" would be the kind of defect nobody reports.
 */
const weekdayNames = (intl: IntlShape, days: readonly (string | null)[], style: 'short' | 'long'): string[] =>
	DAY_ORDER.filter(day => days.indexOf(day) !== -1).map(day => {
		const lower = day.toLowerCase();
		return intl.formatMessage({id: style === 'short' ? `common.${lower}.short` : `common.${lower}`});
	});

const weekdayName = (intl: IntlShape, day: string | null | undefined): string | null =>
	day && DAY_ORDER.indexOf(day) !== -1 ? intl.formatMessage({id: `common.${day.toLowerCase()}`}) : null;

/**
 * A stored calendar day as the app writes dates elsewhere, e.g. `7 Sept 2026`.
 *
 * Parsed as UTC on purpose. A stored date is a plain calendar day with no time or
 * zone; `new Date('2026-09-07')` is midnight UTC, and rendering that in a local
 * zone west of UTC moves it to the 6th.
 *
 * Returns null for a missing or unparseable value so callers render their own
 * placeholder rather than the string `Invalid Date`.
 *
 * `style` picks the fields; `timeZone` is always UTC, for the reason above.
 */
export const formatSetDate = (
	intl: IntlShape,
	iso: string | null | undefined,
	style: Intl.DateTimeFormatOptions = {year: 'numeric', month: 'short', day: 'numeric'}
): string | null => {
	if (!iso) {
		return null;
	}
	const parsed = new Date(`${iso}T00:00:00Z`);
	if (isNaN(parsed.getTime())) {
		return null;
	}
	return intl.formatDate(parsed, {...style, timeZone: 'UTC'});
};

/**
 * The recurrence rule as a phrase, e.g. `Every 2 weeks on Mon, Thu` or
 * `Every month on the second Tuesday`.
 *
 * Returns null when the stored rule is not describable -- an unrecognised
 * frequency, or a monthly rule whose mode is missing. Callers render their own
 * placeholder; this never invents a phrase for data it cannot read, because the
 * phrase is the only thing telling the user what the set will do.
 */
export const describePattern = (intl: IntlShape, set: DescribableTaskSet): string | null => {
	const unitKey = set.frequency ? UNIT_KEY_BY_FREQUENCY[set.frequency] : undefined;
	if (!unitKey) {
		return null;
	}
	// Interval 0 or a negative would render "Every 0 weeks". Treat anything below 1
	// as the single-unit case, which is what an absent interval means anyway.
	const interval = set.intervalLength && set.intervalLength > 1 ? set.intervalLength : 1;
	const pattern = intl.formatMessage({id: unitKey}, {interval});

	if (set.frequency === 'WEEKLY') {
		const names = weekdayNames(intl, set.weekdays || [], 'short');
		// A weekly rule always has at least one day in storage, but a set read back
		// mid-migration or built by hand may not. The bare interval is still true.
		return names.length === 0
			? pattern
			: intl.formatMessage({id: 'task_set.pattern_on_days'}, {pattern, days: names.join(', ')});
	}

	if (set.frequency === 'MONTHLY') {
		if (set.monthlyMode === 'DAY_OF_MONTH') {
			return set.dayOfMonth == null
				? pattern
				: intl.formatMessage({id: 'task_set.pattern_day_of_month'}, {pattern, day: set.dayOfMonth});
		}
		const weekday = weekdayName(intl, set.monthlyWeekday);
		if (set.monthlyMode === 'LAST_WEEKDAY') {
			return weekday == null ? pattern : intl.formatMessage({id: 'task_set.pattern_last_weekday'}, {pattern, weekday});
		}
		if (set.monthlyMode === 'NTH_WEEKDAY') {
			const ordinalKey = set.weekOrdinal == null ? undefined : ORDINAL_KEYS[set.weekOrdinal];
			if (weekday == null || !ordinalKey) {
				return pattern;
			}
			return intl.formatMessage(
				{id: 'task_set.pattern_nth_weekday'},
				{pattern, ordinal: intl.formatMessage({id: ordinalKey}), weekday}
			);
		}
		return pattern;
	}

	return pattern;
};

/**
 * The bound as a phrase, e.g. `26 instances` or `Until 31 Dec 2026`.
 *
 * Returns null for a bound this version cannot describe. Two of the four stored
 * bound types are not creatable yet, and describing one of them as "No end date"
 * would be a claim about the set's lifetime that nothing has established.
 */
export const describeBound = (intl: IntlShape, set: DescribableTaskSet): string | null => {
	if (set.boundType === 'COUNT') {
		// Instances, not the stored count: "Mon, Tue, Wed, 6 times" is 18 instances.
		const count = plannedInstanceCount(set);
		return count == null ? null : intl.formatMessage({id: 'task_set.bound_count'}, {count});
	}
	if (set.boundType === 'DATE') {
		const date = formatSetDate(intl, set.boundEndDate);
		return date === null ? null : intl.formatMessage({id: 'task_set.bound_until'}, {date});
	}
	return null;
};

/** Whether generation has reached the set's stored end. Both dates are ISO, so they compare as strings. */
const generatedToItsBound = (set: DescribableTaskSet): boolean =>
	!!set.materialisedTo && !!set.boundEndDate && set.materialisedTo >= set.boundEndDate;

/**
 * `After T713, 14 Dec`, or null when the last instance is not the set's end.
 *
 * Both halves of the sentence are required: an id with no date, or a date with
 * no id, would print a half-formed phrase, and the bare date below is a better
 * answer than that.
 *
 * A DATE-bounded set must also have GENERATED as far as its bound. Generation
 * runs ahead of today but not necessarily to the bound, and until it gets there
 * the last instance is merely the latest one so far -- naming it would say the
 * set ends weeks before it really does. The watermark is the test rather than
 * the instance's own date, because a bound landing on a day the pattern misses
 * leaves the last instance legitimately earlier than the bound.
 */
const namedEnd = (intl: IntlShape, set: DescribableTaskSet): string | null => {
	const companyTaskId = set.lastInstance?.companyTaskId;
	const date = formatSetDate(intl, set.lastInstance?.startDate);
	if (companyTaskId == null || date === null) {
		return null;
	}
	if (set.boundType === 'DATE' && !generatedToItsBound(set)) {
		return null;
	}
	return intl.formatMessage({id: 'task_set.ends.after_instance'}, {companyTaskId, date});
};

/**
 * When the set runs out, as the instance it ends after: `After T713, 14 Dec`.
 *
 * The instance, not a bare date, and the SAME sentence the edit form's Ends
 * field shows -- one message key, so the two surfaces cannot drift into two
 * formats for one fact. A bound is the last date an instance may START, so a
 * bound landing on a day the pattern misses would otherwise print a date on
 * which nothing happens; naming the instance says what actually ends.
 *
 * Falls back to a bare date, and then to the bound itself, for a set whose
 * last instance is not known -- a set that has generated nothing yet, or one
 * read by a caller that did not select it. Null when the bound cannot be
 * described at all, including a COUNT bound with no count, which
 * `describeBound` also refuses: `boundCount` is nullable in storage, and
 * without that guard a `materialisedTo` left over from generation would print
 * a definite-looking end for a bound that is not actually describable.
 */
export const describeEnd = (intl: IntlShape, set: DescribableTaskSet): string | null => {
	const namedInstance = namedEnd(intl, set);
	if (namedInstance !== null) {
		return namedInstance;
	}
	if (set.boundType === 'DATE') {
		return formatSetDate(intl, set.boundEndDate);
	}
	if (set.boundType !== 'COUNT' || plannedInstanceCount(set) == null) {
		return describeBound(intl, set);
	}
	return formatSetDate(intl, set.materialisedTo) ?? describeBound(intl, set);
};
